import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { LayoutDashboard, Users, FileText, Vote, AlertTriangle, Trophy, ScrollText, Activity } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
    const [overview, setOverview] = useState(null);
    const [logs, setLogs] = useState([]);
    const [contests, setContests] = useState([]);
    const [fraudFlags, setFraudFlags] = useState(null);

    useEffect(() => {
        api.get("/analytics/overview").then((r) => setOverview(r.data));
        api.get("/admin/audit-logs?limit=30").then((r) => setLogs(r.data.logs || []));
        api.get("/contests").then((r) => setContests(r.data.contests || []));
        api.get("/admin/fraud-flags").then((r) => setFraudFlags(r.data));
    }, []);

    return (
        <div className="p-6 lg:p-10">
            <div className="mb-8 fade-up flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] mb-2 flex items-center gap-2 font-semibold"><LayoutDashboard className="w-3 h-3" /> Phòng điều hành</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Bảng điều khiển Ban Tổ chức</h1>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/quan-tri/cuoc-thi/moi" className="btn-gold" data-testid="create-contest-btn">+ Tạo cuộc thi</Link>
                        <Link to="/thong-ke" className="btn-secondary !py-2 !px-4">Xem analytics →</Link>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <KPI icon={<Users />} label="Thí sinh" value={overview?.total_users ?? "—"} link="/quan-tri/nguoi-dung" />
                    <KPI icon={<Trophy />} label="Cuộc thi" value={overview?.total_contests ?? "—"} />
                    <KPI icon={<FileText />} label="Bài thi" value={overview?.total_submissions ?? "—"} />
                    <KPI icon={<AlertTriangle />} label="Cảnh báo" value={(overview?.suspicious_votes ?? 0) + (fraudFlags?.ai_flagged_submissions?.length || 0)} alert />
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Contest management */}
                    <div className="card rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-[var(--gold-deep)]" /> Quản lý cuộc thi</h3>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {contests.map((c) => (
                                <div key={c.id} className="p-3 border border-[var(--border)] rounded-xl hover:border-[var(--gold)] transition">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm truncate text-[var(--navy)]">{c.title}</div>
                                            <div className="text-xs text-slate-500">{c.organizer}</div>
                                        </div>
                                        <span className="badge badge-gold whitespace-nowrap">{c.status}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                                        <span>{c.num_mcq} câu · {c.duration_minutes}p · AI: {c.ai_provider}</span>
                                        <div className="flex gap-2">
                                            <Link to={`/quan-tri/cuoc-thi/${c.id}`} className="text-[var(--navy)] hover:underline font-semibold" data-testid={`edit-contest-${c.id}`}>Sửa</Link>
                                            <Link to={`/quan-tri/cuoc-thi/${c.id}/cau-hoi`} className="text-[var(--gold-deep)] hover:underline font-semibold" data-testid={`manage-questions-${c.id}`}>Đề thi</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Fraud panel */}
                    <div className="card rounded-2xl p-6">
                        <h3 className="font-heading font-semibold flex items-center gap-2 mb-4">
                            <AlertTriangle className="w-4 h-4 text-red-400" /> Cờ cảnh báo Anti-Fraud
                        </h3>
                        <div className="space-y-4">
                            <FraudSection title="Vote nghi ngờ" items={fraudFlags?.suspicious_votes || []}
                                render={(v) => <div className="text-xs"><span className="text-red-400">IP {v.voter_ip}</span> · vote bài {v.submission_id?.slice(0, 8)} · {new Date(v.created_at).toLocaleString("vi-VN")}</div>} />
                            <FraudSection title="Bài viết có cờ AI" items={fraudFlags?.ai_flagged_submissions || []}
                                render={(s) => <div className="text-xs"><span className="text-red-400">{s.id?.slice(0, 8)}</span> · {s.ai_essay_flags?.join(", ")}</div>} />
                        </div>
                    </div>

                    {/* Audit log */}
                    <div className="card rounded-2xl p-6 lg:col-span-2">
                        <h3 className="font-heading font-semibold flex items-center gap-2 mb-4">
                            <ScrollText className="w-4 h-4 text-[var(--gold-deep)]" /> Audit log (30 sự kiện gần nhất)
                        </h3>
                        <div className="overflow-x-auto max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs uppercase tracking-widest text-slate-500">
                                    <tr><th className="text-left p-2">Thời gian</th><th className="text-left p-2">Người</th><th className="text-left p-2">Hành động</th><th className="text-left p-2">Đối tượng</th><th className="text-left p-2">IP</th></tr>
                                </thead>
                                <tbody>
                                    {logs.map((l) => (
                                        <tr key={l.id} className="border-t border-[var(--border)]">
                                            <td className="p-2 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("vi-VN")}</td>
                                            <td className="p-2 text-[var(--gold-deep)] truncate max-w-[180px]">{l.actor_email || "system"}</td>
                                            <td className="p-2 font-mono text-slate-600">{l.action}</td>
                                            <td className="p-2 text-slate-500 font-mono text-xs">{l.target?.slice(0, 12) || "—"}</td>
                                            <td className="p-2 text-slate-500">{l.ip || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
        </div>
    );
}

function KPI({ icon, label, value, alert, link }) {
    const inner = (
        <>
            <div className={`flex items-center gap-2 ${alert ? "text-red-400" : "text-[var(--gold-deep)]"} mb-2 [&_svg]:w-4 [&_svg]:h-4`}>{icon}<span className="text-xs uppercase tracking-widest">{label}</span></div>
            <div className="text-3xl font-heading font-bold">{value}</div>
        </>
    );
    return link
        ? <Link to={link} className="card card-hover p-5 block">{inner}</Link>
        : <div className="card rounded-2xl p-5">{inner}</div>;
}

function FraudSection({ title, items, render }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title} ({items.length})</div>
            {items.length === 0 ? <div className="text-slate-500 text-xs italic">Không có</div>
                : <div className="space-y-2 max-h-32 overflow-y-auto">
                    {items.slice(0, 8).map((it, i) => <div key={i} className="bg-red-400/5 border border-red-400/20 rounded p-2">{render(it)}</div>)}
                </div>}
        </div>
    );
}
